import { lazy, Suspense, type ReactNode } from "react";
import { FEATURES } from "@markfeed/shared";
import { Navigate, Outlet, useLocation, type RouteObject } from "react-router";
import { Loader2 } from "lucide-react";
import { useSession } from "@/hooks/queries";
import { marketingRoutes } from "./marketing";

const AppShell = lazy(() => import("@/components/app/app-shell"));
const Overview = lazy(() => import("@/pages/app/overview"));
const Projects = lazy(() => import("@/pages/app/projects"));
const ProjectDetail = lazy(() => import("@/pages/app/project-detail"));
const Websites = lazy(() => import("@/pages/app/websites"));
const WebsiteDetail = lazy(() => import("@/pages/app/website-detail"));
const Feedback = lazy(() => import("@/pages/app/feedback"));
const Tasks = lazy(() => import("@/pages/app/tasks"));
const Mockups = lazy(() => import("@/pages/app/mockups"));
const MockupDetail = lazy(() => import("@/pages/app/mockup-detail"));
const Team = lazy(() => import("@/pages/app/team"));
const Clients = lazy(() => import("@/pages/app/clients"));
const Notifications = lazy(() => import("@/pages/app/notifications"));
const Billing = lazy(() => import("@/pages/app/billing"));
const Settings = lazy(() => import("@/pages/app/settings"));
const Login = lazy(() => import("@/pages/auth/login"));
const Signup = lazy(() => import("@/pages/auth/signup"));
const VerifyEmail = lazy(() => import("@/pages/auth/verify-email"));
const ForgotPassword = lazy(() => import("@/pages/auth/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/auth/reset-password"));
const Invite = lazy(() => import("@/pages/auth/invite"));
const Onboarding = lazy(() => import("@/pages/auth/onboarding"));
const Review = lazy(() => import("@/pages/review/review"));
const MockupReview = lazy(() => import("@/pages/review/mockup-review"));
const NotFound = lazy(() => import("@/pages/not-found"));
const admin = () => import("@/pages/admin/admin");
const AdminShell = lazy(() => admin().then((m) => ({ default: m.AdminShell })));
const AdminOverview = lazy(() => admin().then((m) => ({ default: m.AdminOverview })));
const AdminOrgs = lazy(() => admin().then((m) => ({ default: m.AdminOrgs })));
const AdminOrgDetail = lazy(() => admin().then((m) => ({ default: m.AdminOrgDetail })));
const AdminUsers = lazy(() => admin().then((m) => ({ default: m.AdminUsers })));
const AdminPayments = lazy(() => admin().then((m) => ({ default: m.AdminPayments })));
const AdminCoupons = lazy(() => admin().then((m) => ({ default: m.AdminCoupons })));
const AdminPlans = lazy(() => admin().then((m) => ({ default: m.AdminPlans })));
const AdminTickets = lazy(() => admin().then((m) => ({ default: m.AdminTickets })));
const AdminSettings = lazy(() => admin().then((m) => ({ default: m.AdminSettings })));

export function FullPageSpinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-label="Loading">
      <Loader2 className="size-6 animate-spin text-brand-600" />
    </div>
  );
}

const S = ({ children }: { children: ReactNode }) => <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;

function RequireAuth({ needOrg = true }: { needOrg?: boolean }) {
  const { data, isLoading } = useSession();
  const loc = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (!data) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (data.user.emailVerificationPending) return <Navigate to="/verify-email" replace />;
  if (needOrg && !data.currentOrgId) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function GuestOnly() {
  const { data, isLoading } = useSession();
  const loc = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (data?.user.emailVerificationPending && !new URLSearchParams(loc.search).get("next")?.startsWith("/invite/")) return <Navigate to="/verify-email" replace />;
  if (data) {
    const next = new URLSearchParams(loc.search).get("next");
    return <Navigate to={next && next.startsWith("/") && !next.startsWith("//") ? next : data.currentOrgId ? "/app" : "/onboarding"} replace />;
  }
  return <Outlet />;
}

export const routes: RouteObject[] = [
  ...marketingRoutes,
  {
    element: <GuestOnly />,
    children: [
      { path: "/login", element: <S><Login /></S> },
      { path: "/signup", element: <S><Signup /></S> },
      { path: "/forgot-password", element: <S><ForgotPassword /></S> },
    ],
  },
  { path: "/verify-email", element: <S><VerifyEmail /></S> },
  { path: "/reset-password", element: <S><ResetPassword /></S> },
  { path: "/invite/:token", element: <S><Invite /></S> },
  { path: "/review/:token", element: <S><Review /></S> },
  ...(FEATURES.mockups ? [{ path: "/m/:token", element: <S><MockupReview /></S> }] : []),
  {
    element: <RequireAuth needOrg={false} />,
    children: [{ path: "/onboarding", element: <S><Onboarding /></S> }],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        path: "/app",
        element: <S><AppShell /></S>,
        children: [
          { index: true, element: <S><Overview /></S> },
          { path: "projects", element: <S><Projects /></S> },
          { path: "projects/:id", element: <S><ProjectDetail /></S> },
          { path: "websites", element: <S><Websites /></S> },
          { path: "websites/:id", element: <S><WebsiteDetail /></S> },
          { path: "feedback", element: <S><Feedback /></S> },
          { path: "feedback/:id", element: <S><Feedback /></S> },
          { path: "tasks", element: <S><Tasks /></S> },
          { path: "mockups", element: <S><Mockups /></S> },
          ...(FEATURES.mockups ? [{ path: "mockups/:id", element: <S><MockupDetail /></S> }] : []),
          { path: "team", element: <S><Team /></S> },
          { path: "clients", element: <S><Clients /></S> },
          { path: "notifications", element: <S><Notifications /></S> },
          { path: "billing", element: <S><Billing /></S> },
          { path: "settings", element: <S><Settings /></S> },
        ],
      },
    ],
  },
  {
    path: "/admin",
    element: <S><AdminShell /></S>,
    children: [
      { index: true, element: <S><AdminOverview /></S> },
      { path: "orgs", element: <S><AdminOrgs /></S> },
      { path: "orgs/:id", element: <S><AdminOrgDetail /></S> },
      { path: "users", element: <S><AdminUsers /></S> },
      { path: "payments", element: <S><AdminPayments /></S> },
      { path: "coupons", element: <S><AdminCoupons /></S> },
      { path: "plans", element: <S><AdminPlans /></S> },
      { path: "tickets", element: <S><AdminTickets /></S> },
      { path: "settings", element: <S><AdminSettings /></S> },
    ],
  },
  { path: "*", element: <S><NotFound /></S> },
];
